import type { ReactNode } from "react";
import { Button, IconButton } from "@no-busy-work/ui";
import { IncidentStatus } from "@no-busy-work/ops-bridge-protocol";
import {
  APP_ENVIRONMENT_OPTIONS,
  APP_OPTIONS,
  BUTTON_TYPE,
  CUSTOM_SERVICE_OPTION,
  UI_TONE
} from "../OpsCenter.Config";
import { incidentCreateCatalog, optionsForApp, ownerDisplayOptions, selectedCatalogAppId } from "../OpsCenter.Catalog";
import { actionRows, actorRefLabel, latestSignal, serviceLabel, statusTone } from "../OpsCenter.Data";
import type { IncidentDetail, IncidentQueueFilters, IncidentQueueItem, LocaleCode, OpsCenterActions, OpsCenterCopy, OpsCenterState } from "../OpsCenter.Types";
import { humanize, statusLabel } from "../i18n/OpsCenter.I18n";
import { Messages, SectionHeader, SelectField, StatusPill, TextAreaField, TextField, submit } from "./OpsCenterScreen.Shared";

interface OpsSectionProps {
  readonly state: OpsCenterState;
  readonly actions: OpsCenterActions;
  readonly copy: OpsCenterCopy;
  readonly locale: LocaleCode;
}

interface IncidentQueueRow {
  readonly id: string;
  readonly appId: string;
  readonly title: string;
  readonly summary: string;
  readonly blocker: string;
  readonly owner: string;
  readonly customerCount: number;
  readonly complaintCount: number;
  readonly environmentKey: string;
  readonly environment: string;
  readonly severity: string;
  readonly statusKey: string;
  readonly status: string;
  readonly selected: boolean;
}

const APP_ID = {
  Apps: "apps",
  Nbw: "nbw"
} as const;

const QUEUE_FILTER_ALL = "all";
const QUEUE_SEVERITY_ORDER: Readonly<Record<string, number>> = {
  SEV0: 0,
  SEV1: 1,
  SEV2: 2,
  SEV3: 3,
  SEV4: 4
};
const QUEUE_SORT = {
  Customers: "customers",
  Priority: "priority",
  Service: "service",
  Status: "status"
} as const;
const QUEUE_STATUS = {
  Identified: IncidentStatus.Identified,
  Mitigating: IncidentStatus.Mitigating,
  Monitoring: IncidentStatus.Monitoring,
  Open: "open",
  Ready: "ready",
  Resolved: IncidentStatus.Resolved
} as const;
const PREVIEW_ENVIRONMENT = "preview";

export function IncidentRail(props: OpsSectionProps): ReactNode {
  const { state, copy } = props;
  return (
    <aside className="incident-rail">
      <div className="incident-rail-top">
        <SectionHeader title={copy.sections.incidents} />
        <IconButton
          aria-expanded={state.createOpen}
          ariaLabel={state.createOpen ? copy.queue.closeCreate : copy.queue.create}
          className={state.createOpen ? "icon-button active" : "icon-button"}
          icon={<span aria-hidden={true}>{state.createOpen ? "x" : "+"}</span>}
          title={state.createOpen ? copy.queue.closeCreate : copy.queue.create}
          type={BUTTON_TYPE.Button}
          onClick={() => props.actions.setCreateOpen(!state.createOpen)}
        />
      </div>
      {state.createOpen ? <CreateIncidentForm {...props} /> : null}
      <IncidentSearch {...props} />
      <IncidentQueueList {...props} />
      <Messages state={state} />
    </aside>
  );
}

function IncidentSearch({ state, actions, copy }: OpsSectionProps): ReactNode {
  return (
    <section className="incident-search" aria-label={copy.forms.incidentSearch}>
      <TextField
        label={copy.forms.incidentSearch}
        name="incidentSearch"
        type="search"
        value={state.lookupIncidentId}
        onChange={actions.setLookupIncidentId}
      />
      <div className="incident-filter-grid">
        <SelectField label={copy.forms.app} name="queueApp" options={queueAppOptions(copy)} value={state.queueFilters.appId} onChange={(value) => actions.setQueueFilter("appId", value)} />
        <SelectField label={copy.forms.environment} name="queueEnvironment" options={queueEnvironmentOptions(copy)} value={state.queueFilters.environment} onChange={(value) => actions.setQueueFilter("environment", value)} />
        <SelectField label={copy.labels.severity} name="queueSeverity" options={queueSeverityOptions(copy)} value={state.queueFilters.severity} onChange={(value) => actions.setQueueFilter("severity", value)} />
        <SelectField label={copy.labels.status} name="queueStatus" options={queueStatusOptions(copy)} value={state.queueFilters.status} onChange={(value) => actions.setQueueFilter("status", value)} />
        <SelectField label={copy.queue.sort} name="queueSort" options={queueSortOptions(copy)} value={state.queueFilters.sort} onChange={(value) => actions.setQueueFilter("sort", value)} />
      </div>
    </section>
  );
}

function IncidentQueueList({ state, actions, copy }: OpsSectionProps): ReactNode {
  const rows = incidentQueueRows(state, copy);
  const hasQueueConstraint = state.lookupIncidentId.trim() || queueHasActiveFilters(state.queueFilters);
  return (
    <section className="incident-queue" aria-label={copy.queue.listTitle}>
      <div className="incident-queue-toolbar">
        <span>{copy.queue.priorityEvents}</span>
        <StatusPill label={String(rows.length)} tone={UI_TONE.Neutral} />
      </div>
      <div className="incident-queue-list">
        {rows.length ? (
          rows.map((row) => (
            <button
              className={row.selected ? "incident-queue-row selected" : "incident-queue-row"}
              key={row.id}
              type={BUTTON_TYPE.Button}
              onClick={() => void actions.loadKnownIncident(row.id)}
            >
              <span className="incident-queue-row-top">
                <strong>{row.title}</strong>
                <StatusPill label={row.status} tone={statusTone(row.statusKey)} />
              </span>
              <span className="incident-queue-summary">{row.summary}</span>
              <span className="incident-queue-signals">
                <span>{row.customerCount} {copy.labels.impacted}</span>
                <span>{row.complaintCount} {copy.labels.complaints}</span>
                <span>{copy.labels.currentBlocker}: {row.blocker}</span>
              </span>
              <span className="incident-queue-meta">
                <span>{queueAppLabel(row.appId)}</span>
                <span>{row.environment}</span>
                <span>{row.severity}</span>
                <span>{copy.labels.owner}: {row.owner}</span>
              </span>
            </button>
          ))
        ) : (
          <article className="incident-queue-empty">
            <h2>{hasQueueConstraint ? copy.queue.noMatches : copy.queue.title}</h2>
            <p>{hasQueueConstraint ? copy.queue.noMatchesBody : copy.queue.noKnownIncidents}</p>
          </article>
        )}
      </div>
    </section>
  );
}

function incidentQueueRows(state: OpsCenterState, copy: OpsCenterCopy): readonly IncidentQueueRow[] {
  const active = state.incident
    ? [{
        id: state.incident.incident.id,
        appId: appIdForService(state.incident.incident.serviceId),
        title: serviceLabel(state.incident.incident.serviceId, copy),
        summary: latestSignal(state.incident) ?? state.incident.incident.fingerprint,
        blocker: currentBlocker(state.incident, copy),
        owner: primaryOwner(state.incident, copy),
        customerCount: state.incident.communicationSummary.impactedCustomerCount,
        complaintCount: state.incident.communicationSummary.complaintCount,
        environmentKey: state.incident.incident.environment,
        environment: humanize(state.incident.incident.environment),
        severity: state.incident.incident.severity.toUpperCase(),
        statusKey: state.incident.incident.status,
        status: statusLabel(copy, state.incident.incident.status),
        selected: true
      }]
    : [];
  const fixtureIds = state.health?.previewFixtures?.incidentIds ?? [];
  const selectedIncidentId = state.incident?.incident.id ?? "";
  const fixtures = fixtureIds
    .filter((incidentId) => incidentId !== selectedIncidentId)
    .map((incidentId) => previewIncidentQueueRow(incidentId, state.health?.previewFixtures?.environment ?? PREVIEW_ENVIRONMENT, copy));
  const serverRows = state.incidentQueue
    .filter((incident) => incident.id !== selectedIncidentId)
    .map((incident) => serverIncidentQueueRow(incident, copy));
  const query = state.lookupIncidentId.trim().toLowerCase();
  const rows = [...active, ...(serverRows.length ? serverRows : fixtures)];
  const filteredRows = rows.filter((row) => rowMatchesQueue(row, query, state.queueFilters));
  return sortQueueRows(filteredRows, state.queueFilters.sort);
}

function serverIncidentQueueRow(incident: IncidentQueueItem, copy: OpsCenterCopy): IncidentQueueRow {
  return {
    id: incident.id,
    appId: incident.appId,
    title: serviceLabel(incident.serviceId, copy),
    summary: incident.summary,
    blocker: queueBlockerLabel(incident.blocker, copy),
    owner: actorRefLabel(incident.ownerRef, copy),
    customerCount: incident.customerCount,
    complaintCount: incident.complaintCount,
    environmentKey: incident.environment,
    environment: humanize(incident.environment),
    severity: incident.severity.toUpperCase(),
    statusKey: incident.status,
    status: statusLabel(copy, incident.status),
    selected: false
  };
}

function previewIncidentQueueRow(incidentId: string, environment: string, copy: OpsCenterCopy): IncidentQueueRow {
  const meta = previewIncidentQueueMeta(incidentId, copy);
  return {
    id: incidentId,
    appId: meta.appId,
    title: meta.title,
    summary: previewIncidentQueueSummary(meta, copy),
    blocker: meta.blocker,
    owner: meta.owner,
    customerCount: meta.customerCount,
    complaintCount: meta.complaintCount,
    environmentKey: environment,
    environment: humanize(environment),
    severity: meta.severity,
    statusKey: meta.statusKey,
    status: meta.status,
    selected: false
  };
}

interface PreviewIncidentQueueMeta {
  readonly appId: string;
  readonly title: string;
  readonly blocker: string;
  readonly owner: string;
  readonly customerCount: number;
  readonly complaintCount: number;
  readonly severity: string;
  readonly statusKey: string;
  readonly status: string;
}

function previewIncidentQueueMeta(incidentId: string, copy: OpsCenterCopy): PreviewIncidentQueueMeta {
  const metaByIncidentId: Readonly<Record<string, PreviewIncidentQueueMeta>> = {
    incident_preview_phone_lines: {
      appId: APP_ID.Nbw,
      title: serviceLabel("communications.phone_lines", copy),
      blocker: copy.labels.outageNoticeApproval,
      owner: "voice",
      customerCount: 42,
      complaintCount: 7,
      severity: "SEV1",
      statusKey: QUEUE_STATUS.Mitigating,
      status: copy.status.mitigating
    },
    incident_preview_email_latency: {
      appId: APP_ID.Nbw,
      title: serviceLabel("provider.email_delivery", copy),
      blocker: copy.labels.noActiveBlockers,
      owner: "growth",
      customerCount: 18,
      complaintCount: 1,
      severity: "SEV3",
      statusKey: QUEUE_STATUS.Monitoring,
      status: copy.status.monitoring
    },
    incident_preview_database_latency: {
      appId: APP_ID.Nbw,
      title: serviceLabel("database.primary", copy),
      blocker: copy.labels.deliveryRisk,
      owner: "platform",
      customerCount: 11,
      complaintCount: 2,
      severity: "SEV2",
      statusKey: QUEUE_STATUS.Identified,
      status: copy.status.identified
    },
    incident_preview_public_api_outage: {
      appId: APP_ID.Apps,
      title: serviceLabel("apps.api", copy),
      blocker: copy.labels.approvalRequired,
      owner: "platform",
      customerCount: 26,
      complaintCount: 3,
      severity: "SEV1",
      statusKey: QUEUE_STATUS.Identified,
      status: copy.status.identified
    },
    incident_preview_floating_ip_misassignment: {
      appId: APP_ID.Nbw,
      title: serviceLabel("infra.floating_ip", copy),
      blocker: copy.labels.approvalRequired,
      owner: "infra",
      customerCount: 9,
      complaintCount: 1,
      severity: "SEV1",
      statusKey: QUEUE_STATUS.Identified,
      status: copy.status.identified
    },
    incident_preview_vps_degradation: {
      appId: APP_ID.Nbw,
      title: serviceLabel("infra.vps", copy),
      blocker: copy.labels.approvalRequired,
      owner: "infra",
      customerCount: 14,
      complaintCount: 2,
      severity: "SEV2",
      statusKey: QUEUE_STATUS.Mitigating,
      status: copy.status.mitigating
    },
    incident_preview_caddy_hosting_failure: {
      appId: APP_ID.Nbw,
      title: serviceLabel("edge.caddy", copy),
      blocker: copy.labels.approvalRequired,
      owner: "platform",
      customerCount: 7,
      complaintCount: 0,
      severity: "SEV2",
      statusKey: QUEUE_STATUS.Identified,
      status: copy.status.identified
    },
    incident_preview_postgres_role_conflict: {
      appId: APP_ID.Apps,
      title: serviceLabel("database.primary", copy),
      blocker: copy.labels.approvalRequired,
      owner: "database",
      customerCount: 22,
      complaintCount: 3,
      severity: "SEV1",
      statusKey: QUEUE_STATUS.Identified,
      status: copy.status.identified
    },
    incident_preview_github_delivery_failure: {
      appId: APP_ID.Nbw,
      title: serviceLabel("github.delivery", copy),
      blocker: copy.labels.approvalRequired,
      owner: "delivery",
      customerCount: 0,
      complaintCount: 0,
      severity: "SEV3",
      statusKey: QUEUE_STATUS.Identified,
      status: copy.status.identified
    },
    incident_preview_llm_degradation: {
      appId: APP_ID.Apps,
      title: serviceLabel("apps.model_runtime", copy),
      blocker: copy.labels.noActiveBlockers,
      owner: "ai-platform",
      customerCount: 12,
      complaintCount: 2,
      severity: "SEV2",
      statusKey: QUEUE_STATUS.Monitoring,
      status: copy.status.monitoring
    },
    incident_preview_safe_browsing_warning: {
      appId: APP_ID.Nbw,
      title: serviceLabel("safe_browsing.domain", copy),
      blocker: copy.labels.approvalRequired,
      owner: "security",
      customerCount: 6,
      complaintCount: 1,
      severity: "SEV2",
      statusKey: QUEUE_STATUS.Identified,
      status: copy.status.identified
    },
    incident_preview_control_plane_partial_outage: {
      appId: APP_ID.Nbw,
      title: serviceLabel("ops.control_plane", copy),
      blocker: copy.labels.noActiveBlockers,
      owner: "ops",
      customerCount: 0,
      complaintCount: 0,
      severity: "SEV2",
      statusKey: QUEUE_STATUS.Monitoring,
      status: copy.status.monitoring
    },
    incident_preview_middleware_error: {
      appId: APP_ID.Apps,
      title: serviceLabel("apps.middleware", copy),
      blocker: copy.labels.approvalRequired,
      owner: "apps",
      customerCount: 16,
      complaintCount: 4,
      severity: "SEV2",
      statusKey: QUEUE_STATUS.Identified,
      status: copy.status.identified
    }
  };
  const meta = metaByIncidentId[incidentId];
  if (meta) {
    return meta;
  }
  return {
    appId: APP_ID.Nbw,
    title: queueIncidentTitle(incidentId),
    blocker: copy.labels.noActiveBlockers,
    owner: copy.labels.pending,
    customerCount: 0,
    complaintCount: 0,
    severity: copy.status.ready,
    statusKey: QUEUE_STATUS.Ready,
    status: copy.status.ready
  };
}

function previewIncidentQueueSummary(meta: PreviewIncidentQueueMeta, copy: OpsCenterCopy): string {
  return [
    `${meta.customerCount} ${copy.labels.impacted}`,
    `${meta.complaintCount} ${copy.labels.complaints}`,
    `${copy.labels.currentBlocker}: ${meta.blocker}`
  ].join(" / ");
}

function rowMatchesQueue(row: IncidentQueueRow, query: string, filters: IncidentQueueFilters): boolean {
  const queryMatches = !query || [
    row.id,
    row.title,
    row.summary,
    row.blocker,
    row.owner,
    row.appId,
    row.environment,
    row.environmentKey,
    row.severity,
    row.status,
    String(row.customerCount),
    String(row.complaintCount)
  ].some((value) => value.toLowerCase().includes(query));
  return queryMatches
    && optionMatches(filters.appId, row.appId)
    && optionMatches(filters.environment, row.environmentKey)
    && optionMatches(filters.severity, row.severity)
    && optionMatches(filters.status, row.statusKey);
}

function queueHasActiveFilters(filters: IncidentQueueFilters): boolean {
  return filters.appId !== QUEUE_FILTER_ALL
    || filters.environment !== QUEUE_FILTER_ALL
    || filters.severity !== QUEUE_FILTER_ALL
    || filters.status !== QUEUE_FILTER_ALL;
}

function optionMatches(filterValue: string, rowValue: string): boolean {
  return filterValue === QUEUE_FILTER_ALL || filterValue === rowValue;
}

function sortQueueRows(rows: readonly IncidentQueueRow[], sort: IncidentQueueFilters["sort"]): readonly IncidentQueueRow[] {
  return [...rows].sort((left, right) => {
    if (sort === QUEUE_SORT.Customers) {
      return right.customerCount - left.customerCount || right.complaintCount - left.complaintCount || comparePriority(left, right);
    }
    if (sort === QUEUE_SORT.Status) {
      return left.status.localeCompare(right.status) || comparePriority(left, right);
    }
    if (sort === QUEUE_SORT.Service) {
      return left.title.localeCompare(right.title) || comparePriority(left, right);
    }
    return comparePriority(left, right);
  });
}

function comparePriority(left: IncidentQueueRow, right: IncidentQueueRow): number {
  return severityRank(left.severity) - severityRank(right.severity)
    || right.customerCount - left.customerCount
    || right.complaintCount - left.complaintCount
    || left.title.localeCompare(right.title);
}

function severityRank(severity: string): number {
  return QUEUE_SEVERITY_ORDER[severity.toUpperCase()] ?? 99;
}

function queueAppOptions(copy: OpsCenterCopy): readonly (readonly [string, string])[] {
  return [[QUEUE_FILTER_ALL, copy.queue.allApps], ...APP_OPTIONS];
}

function queueEnvironmentOptions(copy: OpsCenterCopy): readonly (readonly [string, string])[] {
  const seen = new Set<string>();
  const options = Object.values(APP_ENVIRONMENT_OPTIONS)
    .flat()
    .filter(([value]) => {
      if (seen.has(value)) {
        return false;
      }
      seen.add(value);
      return true;
    });
  return [[QUEUE_FILTER_ALL, copy.queue.allEnvironments], ...options];
}

function queueSeverityOptions(copy: OpsCenterCopy): readonly (readonly [string, string])[] {
  return [
    [QUEUE_FILTER_ALL, copy.queue.allSeverities],
    ["SEV0", "SEV0"],
    ["SEV1", "SEV1"],
    ["SEV2", "SEV2"],
    ["SEV3", "SEV3"],
    ["SEV4", "SEV4"]
  ];
}

function queueStatusOptions(copy: OpsCenterCopy): readonly (readonly [string, string])[] {
  return [
    [QUEUE_FILTER_ALL, copy.queue.allStatuses],
    [QUEUE_STATUS.Identified, copy.status.identified],
    [QUEUE_STATUS.Mitigating, copy.status.mitigating],
    [QUEUE_STATUS.Monitoring, copy.status.monitoring],
    [QUEUE_STATUS.Open, copy.status.open],
    [QUEUE_STATUS.Resolved, copy.status.resolved],
    [QUEUE_STATUS.Ready, copy.status.ready]
  ];
}

function queueSortOptions(copy: OpsCenterCopy): readonly (readonly [string, string])[] {
  return [
    [QUEUE_SORT.Priority, copy.queue.sortPriority],
    [QUEUE_SORT.Customers, copy.queue.sortCustomers],
    [QUEUE_SORT.Status, copy.queue.sortStatus],
    [QUEUE_SORT.Service, copy.queue.sortService]
  ];
}

function queueAppLabel(appId: string): string {
  return APP_OPTIONS.find(([value]) => value === appId)?.[1] ?? humanize(appId);
}

function appIdForService(serviceId: string): string {
  return serviceId.startsWith("apps.") ? APP_ID.Apps : APP_ID.Nbw;
}

function primaryOwner(detail: IncidentDetail, copy: OpsCenterCopy): string {
  const ownerRef = detail.customerImpact[0]?.ownerRef ?? detail.ownerEscalations[0]?.recipientRef ?? "";
  return ownerRef ? actorRefLabel(ownerRef, copy) : copy.labels.pending;
}

function currentBlocker(detail: IncidentDetail, copy: OpsCenterCopy): string {
  if (detail.communicationSummary.outageNoticeState === "draft") {
    return copy.labels.outageNoticeApproval;
  }
  if (detail.communicationSummary.deliveryRiskCount > 0) {
    return copy.labels.deliveryRisk;
  }
  if (actionRows(detail, copy).some((row) => row.requiresApproval)) {
    return copy.labels.approvalRequired;
  }
  return copy.labels.noActiveBlockers;
}

function queueIncidentTitle(incidentId: string): string {
  const normalized = incidentId.replace(/^incident[_:-]?preview[_:-]?/i, "").replace(/^preview[_:-]?/i, "");
  return normalized ? humanize(normalized) : incidentId;
}

function queueBlockerLabel(blocker: string, copy: OpsCenterCopy): string {
  if (blocker === "outage_notice_approval") {
    return copy.labels.outageNoticeApproval;
  }
  if (blocker === "resolution_delivery_risk") {
    return copy.labels.deliveryRisk;
  }
  if (blocker === "approval_required") {
    return copy.labels.approvalRequired;
  }
  return copy.labels.noActiveBlockers;
}

function CreateIncidentForm({ state, actions, copy }: OpsSectionProps): ReactNode {
  const catalog = incidentCreateCatalog(state.health);
  const appId = selectedCatalogAppId(state.create.appId, catalog);
  const serviceOptions = optionsForApp(catalog.servicesByApp, appId);
  return (
    <form className="compact-form incident-create-form" onSubmit={(event) => submit(event, actions.createIncident)}>
      <TextAreaField label={copy.forms.summary} name="rawSummary" rows={4} value={state.create.rawSummary} onChange={(value) => actions.setCreateField("rawSummary", value)} />
      <SelectField label={copy.forms.app} name="appId" options={catalog.apps} value={appId} onChange={(value) => actions.setCreateField("appId", value)} />
      <SelectField label={copy.forms.service} name="serviceHint" options={serviceOptions} value={state.create.serviceHint} onChange={(value) => actions.setCreateField("serviceHint", value)} />
      {state.create.serviceHint === CUSTOM_SERVICE_OPTION ? (
        <TextField label={copy.forms.customService} name="customServiceHint" type="text" value={state.create.customServiceHint} onChange={(value) => actions.setCreateField("customServiceHint", value)} />
      ) : null}
      <SelectField label={copy.forms.environment} name="environment" options={optionsForApp(catalog.environmentsByApp, appId)} value={state.create.environment} onChange={(value) => actions.setCreateField("environment", value)} />
      <SelectField label={copy.forms.source} name="source" options={catalog.sources} value={state.create.source} onChange={(value) => actions.setCreateField("source", value)} />
      <SelectField label={copy.labels.severity} name="severity" options={catalog.severities} value={state.create.severity} onChange={(value) => actions.setCreateField("severity", value)} />
      <SelectField label={copy.labels.owner} name="ownerRef" options={ownerDisplayOptions(catalog.owners, copy)} value={state.create.ownerRef} onChange={(value) => actions.setCreateField("ownerRef", value)} />
      <SelectField label={copy.forms.locale} name="customerLocale" options={catalog.locales} value={state.create.customerLocale} onChange={(value) => actions.setCreateField("customerLocale", value)} />
      <SelectField label={copy.forms.audience} name="customerAudienceRef" options={catalog.audiences} value={state.create.customerAudienceRef} onChange={(value) => actions.setCreateField("customerAudienceRef", value)} />
      <Button className="primary-button" disabled={state.busy} isLoading={state.busy} loadingLabel={copy.auth.working} type={BUTTON_TYPE.Submit} variant="primary">
        {state.busy ? copy.auth.working : copy.forms.createIncident}
      </Button>
    </form>
  );
}
